'use client';

import { FormProvider, useForm } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Divider, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { FormFiled } from '../../../../../../components/formFiledV2/FormFiled';
import { TextField } from '../../../../../../components/textField/TextField';
import {
  availablePersonalDataDisclosure,
  availablePhonePrefixes,
  availableRelationWithOrganization,
  REGULATION_VALUE,
  signallerFormSchema,
  SUPPORTED_PREFIXES,
} from '../../configs/signallerForm.config';
import { Autocomplete } from '../../../../../../components/autocomplete/Autocomplete';
import { Button } from '../../../../../../components/button/Button';
import { PhoneField } from '../../../../../../components/phoneField/PhoneField';
import {
  APP_ENABLE_ANONYMOUS_REPORT,
  APP_PARTNER_FULL_NAME,
  APP_PARTNER_SIGNALLER_DATA_PROTECTION_REGULATION,
  APP_PARTNER_SIGNALLER_PERSONAL_DATA_REGULATION,
  APP_PARTNER_SIGNALLER_REGULATION,
  APP_PARTNER_SIGNALLER_REGULATION_ST,
  LEAVE_ALERT_SS_KEY,
} from '../../../../../../configs/app.config';
import { UploadFiles } from '../../../../../../components/uploadFiles/UploadFiles';
import { CheckboxList } from '../../../../../../components/checkboxList/CheckboxList';
import { getSubjects } from '../../../../../../services/api/signaller/signaller.service';
import { isClient } from '../../../../../../utils/next/next.util';
import {
  availablePartnerFullNames,
  isMultiPartner,
} from '../../../../../../utils/partner/partner.util';
import { handleSetPartnerId } from '../../../../../../handlers/partner/partner.handler';
import { Switch } from '../../../../../../components/switch/Switch';

import type { ISignallerFormProps, ISignallerFormState } from './signallerForm.type';

const gridWithoutTooltipSx = { pr: 5 };

const underline = (chunks: ReactNode): ReactNode => <u>{chunks}</u>;

export const SignallerForm = ({
  handleSubmit: onSubmit,
  locale,
  showPassword,
}: ISignallerFormProps) => {
  const [formError, setFromError] = useState<string>('');
  const [isAnonim, setIsAnonim] = useState<boolean>(false);

  const t = useTranslations();
  const alertFlag = isClient() ? sessionStorage.getItem(LEAVE_ALERT_SS_KEY) === 'true' : false;

  const form = useForm<ISignallerFormState>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    defaultValues: {
      reporter: '',
      partnerNames: [],
      subject: [],
      description: '',
      reportedPersson: '',
      witness: '',
      evidence: '',
      evidenceFiles: [],
      email: '',
      phone: [SUPPORTED_PREFIXES.polish, ''],
      personalDataDisclosure: [],
      password: '',
      confirmPassword: '',
      regulation: [],
      relationWithOrganization: [],
      street: '',
      zip: '',
      city: '',
      country: '',
    },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    resolver: yupResolver(signallerFormSchema) as any,
  });

  const { data: availableSubjects, isFetching } = useQuery({
    queryFn: () => getSubjects(),
    queryKey: ['getSubjects', locale],
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = form;

  useEffect(() => {
    if (isDirty !== alertFlag) {
      sessionStorage.setItem(LEAVE_ALERT_SS_KEY, isDirty.toString());
    }
  }, [alertFlag, isDirty]);

  const regulation = `
  ${t('labels.formRegulation')}
  ${
    APP_PARTNER_SIGNALLER_REGULATION_ST &&
    t('labels.formRegulationLabel0', { signallerRegulationSt: APP_PARTNER_SIGNALLER_REGULATION_ST })
  }
  ${
    APP_PARTNER_SIGNALLER_REGULATION &&
    t('labels.formRegulationLabel1', { signallerRegulation: APP_PARTNER_SIGNALLER_REGULATION })
  }
  ${
    APP_PARTNER_SIGNALLER_PERSONAL_DATA_REGULATION &&
    t('labels.formRegulationLabel2', {
      signallerPersonalDataRegulation: APP_PARTNER_SIGNALLER_PERSONAL_DATA_REGULATION,
    })
  }
  ${
    APP_PARTNER_SIGNALLER_DATA_PROTECTION_REGULATION &&
    t('labels.formRegulationLabel3', {
      signallerDataProtectionRegulation: APP_PARTNER_SIGNALLER_DATA_PROTECTION_REGULATION,
    })
  }
`;

  return (
    <FormProvider {...form}>
      <form
        name="signallerForm"
        // look: https://react-hook-form.com/docs/useformcontext
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(async (data) => {
          await onSubmit(data, {
            handleReset: reset,
            handleSetError: setFromError,
            availableSubjects: availableSubjects || [],
          });
        })}
      >
        <Typography variant="h5">{t('text.formTitle')}</Typography>
        <Typography variant="body1">{t('text.formTooltip')}</Typography>

        <Grid container spacing={2} rowSpacing={4} sx={{ p: { xs: 1, md: 2 } }}>
          {isMultiPartner() && (
            <>
              <Grid size={12}>
                <Typography variant="h5">{t('headers.companyData')}</Typography>
              </Grid>

              <Grid size={12}>
                <Grid sx={{ pr: { xs: 5, md: 6 } }} size={{ xs: 12, md: 6 }}>
                  <FormFiled
                    name="partnerNames"
                    label={t('labels.formPartnerName')}
                    Component={Autocomplete}
                    componentProps={{ availableValues: availablePartnerFullNames() }}
                    handleChangeCallback={({ value }) => {
                      const selectedPartner = availablePartnerFullNames().find(
                        (partner) => partner.value === value,
                      );

                      if (selectedPartner) {
                        const chosenPartnerId = selectedPartner.value;
                        const chosenPartnerFullName = selectedPartner.label;

                        handleSetPartnerId({ chosenPartnerId, chosenPartnerFullName });
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Grid size={12}>
            <Typography variant="h5">{t('headers.reporterData')}</Typography>
          </Grid>
          {APP_ENABLE_ANONYMOUS_REPORT && (
            <Grid size={12}>
              <Switch
                name="isAnonim"
                value={isAnonim}
                label={t('labels.formAnonim')}
                handleChange={({ value }) => {
                  setIsAnonim(value);
                  form.resetField('reporter');
                }}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 6 }}>
            <FormFiled
              name="reporter"
              label={
                APP_ENABLE_ANONYMOUS_REPORT
                  ? t('labels.formReporter')
                  : t('labels.formReporterObligatory')
              }
              tooltip={t('labels.formReporterTooltip', {
                company: APP_PARTNER_FULL_NAME.toUpperCase(),
              })}
              helperText={APP_ENABLE_ANONYMOUS_REPORT ? t('text.formReporterDescription') : ''}
              Component={TextField}
              disabled={isAnonim}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormFiled
              name="relationWithOrganization"
              label={t('labels.relationWithOrganization')}
              tooltip={t('text.relationWithOrganizationTooltip')}
              Component={Autocomplete}
              componentProps={{ availableValues: availableRelationWithOrganization(t) }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormFiled
              name="email"
              label={t('labels.formEmail')}
              tooltip={t('labels.formEmailTooltip')}
              Component={TextField}
              helperText={t('text.formEmailDescription')}
            />
          </Grid>
          <Grid sx={gridWithoutTooltipSx} size={{ xs: 12, md: 6 }}>
            <FormFiled
              name="phone"
              label={t('labels.formPhone')}
              Component={PhoneField}
              componentProps={{ availableValues: availablePhonePrefixes }}
            />
          </Grid>

          <Grid size={12}>
            <Divider />
            <Typography>{t('headers.correspondenceAddress')}</Typography>
          </Grid>
          <Grid sx={gridWithoutTooltipSx} size={{ xs: 12, md: 6 }}>
            <FormFiled name="street" label={t('labels.formStreet')} Component={TextField} />
          </Grid>
          <Grid sx={gridWithoutTooltipSx} size={{ xs: 12, md: 6 }}>
            <FormFiled name="zip" label={t('labels.formZip')} Component={TextField} />
          </Grid>
          <Grid sx={gridWithoutTooltipSx} size={{ xs: 12, md: 6 }}>
            <FormFiled name="city" label={t('labels.formCity')} Component={TextField} />
          </Grid>
          <Grid sx={gridWithoutTooltipSx} size={{ xs: 12, md: 6 }}>
            <FormFiled name="country" label={t('labels.formCountry')} Component={TextField} />
          </Grid>

          <Grid size={12}>
            <Typography variant="h5">{t('headers.reportData')}</Typography>
          </Grid>

          <Grid container size={12}>
            <Grid sx={{ pr: { xs: 5, md: 6 } }} size={{ xs: 12, md: 6 }}>
              <FormFiled
                name="subject"
                label={t('labels.formSubject')}
                Component={Autocomplete}
                componentProps={{ availableValues: availableSubjects || [], loading: isFetching }}
              />
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormFiled
              name="reportedPersson"
              label={t('labels.formReportedPerson')}
              tooltip={t('labels.formReportedPersonTooltip')}
              Component={TextField}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormFiled
              name="witness"
              label={t('labels.formWitness')}
              Component={TextField}
              tooltip={t('labels.formWitnessTooltip')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormFiled
              name="description"
              label={t('labels.formDescription')}
              tooltip={t('labels.formDescriptionTooltip')}
              Component={TextField}
              componentProps={{ multiline: true, minRows: 4, maxRows: 10 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormFiled
              name="evidence"
              label={t('labels.formEvidence')}
              tooltip={t('labels.formEvidenceTooltip')}
              Component={TextField}
              componentProps={{ multiline: true, minRows: 4, maxRows: 10 }}
            />
          </Grid>

          <Grid container justifyContent="center" size={12}>
            <Grid sx={{ pr: { xs: 5, md: 6 } }} size={{ xs: 12, md: 8, lg: 7, xl: 6 }}>
              <FormFiled
                name="evidenceFiles"
                label={t('labels.formAttachments')}
                Component={UploadFiles}
                helperText={t('text.formAttachmentsTooltip')}
              />
            </Grid>
          </Grid>

          <Grid size={12}>
            <Typography variant="h5">{t('headers.reportProtection')}</Typography>
          </Grid>

          {showPassword && (
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormFiled
                  name="password"
                  label={t('labels.formPassword')}
                  tooltip={t('labels.formPasswordTooltip')}
                  helperText={t('text.formPasswordDescription')}
                  Component={TextField}
                  componentProps={{ type: 'password', autoComplete: 'current-password' }}
                />
              </Grid>

              <Grid sx={gridWithoutTooltipSx} size={{ xs: 12, md: 6 }}>
                <FormFiled
                  name="confirmPassword"
                  label={t('labels.formConfirmPassword')}
                  Component={TextField}
                  componentProps={{ type: 'password', autoComplete: 'current-password' }}
                />
              </Grid>
            </>
          )}

          <Grid container size={12}>
            <Grid sx={{ pr: { xs: 0, md: 1 } }} size={{ xs: 12, md: 6 }}>
              <FormFiled
                name="personalDataDisclosure"
                label={t('labels.personalDataDisclosure')}
                Component={Autocomplete}
                tooltip={t('labels.requiredMessageTooltip')}
                componentProps={{ availableValues: availablePersonalDataDisclosure(t) }}
                helperText={t('text.requiredMessageDescription')}
              />
            </Grid>
          </Grid>

          <Grid size={12}>
            <Typography variant="h5">{t('headers.consents')}</Typography>
          </Grid>

          <Grid sx={gridWithoutTooltipSx} size={{ xs: 12, lg: 7 }}>
            <FormFiled
              name="regulation"
              label=""
              Component={CheckboxList}
              componentProps={{ availableValues: [{ value: REGULATION_VALUE, label: regulation }] }}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">{t.rich('text.formDisclaimer', { u: underline })}</Typography>
          </Grid>

          <Grid sx={{ display: 'flex', justifyContent: 'center' }} size={12}>
            <Button
              variant="outlined"
              size="large"
              sx={{ minWidth: 100, mr: 2 }}
              handleClick={() => {
                reset();
              }}
              disabled={isSubmitting}
            >
              {t('labels.clear')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{ minWidth: 200 }}
              loading={isSubmitting}
            >
              {t('labels.report')}
            </Button>

            {formError && (
              <Typography sx={{ mt: 2 }} color="error">
                {formError}
              </Typography>
            )}
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};
